import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Services } from '@/services/services'

import './Main.css'
import { Translates } from '@/services/repository/i18n'
import { MainPageLayout } from '@/components/layout/MainLayout'
import { Machine } from '@/services/model/machine'
import { CurrentSession } from '@/services/repository/session'
import { CleanWizardData, LoadWizardData, SaveWizardData, WizardData } from './Model'
import { BackButton } from '@/components/layout/NavBar'
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition'
import { email, phoneNumber } from '@/components/forms/users/User'

function validateEmail(email: string): boolean {
  // Simple RFC 5322 Official Standard
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}
function validatePhone(phone: string | undefined): boolean {
  // allow empty, but if not, only digits
  if (!phone) return true
  return /^\d+$/.test(phone)
}

export const SupportWizardStep5 = ({
  services,
  session
}: {
  services: Services
  session?: CurrentSession
}) => {
  const navigate = useNavigate()
  const [translates, setTranslates] = useState<Translates>()
  const [done, setDone] = useState<boolean>()
  const [error, setError] = useState<string>()
  const [wizardData, setWizardData] = useState<WizardData>(LoadWizardData())
  const [emailError, setEmailError] = useState<string>()
  const [phoneError, setPhoneError] = useState<string>()

  useEffect(() => {
    if (session) {
      setTranslates(session.translates)
      setWizardData((prev: WizardData) => {
        let newData = {
          ...prev,
          email: prev.email ? prev.email : session.user.email,
          phoneNumber: prev.phoneNumber ? prev.phoneNumber : session.user.phoneNumber
        }
        SaveWizardData(newData)
        return newData
      })
    } else {
      services.i18n.get('main').then(setTranslates)
    }
  }, [session])

  if (
    !translates ||
    !wizardData
  ) {
    return <div className='spinner'></div>
  }

  return (
    <MainPageLayout
      pageName='support-wizard'
      services={services}
      translates={translates}
      session={session}
      navbarLeft={
        <BackButton
          services={services}
          translates={translates}
          replaceUrl={done ? `/communal/support/wizard/page4` : undefined}
        />
      }
    >
      <h2 className='page-title'>{translates.for(`wizards.support_question.title`)}</h2>
      {!done ? <div className='main-form'>
        <div className='form-field'>
          <label>{translates.for('wizards.support_question.email')}</label>
          <input
            type='email'
            value={wizardData.email}
            readOnly={!!session?.user}
            onChange={(e) => {
              const val = e.target.value
              setWizardData(prev => {
                if (!prev) return prev
                const data = { ...prev, email: val }
                SaveWizardData(data)
                return data
              })
              setEmailError(undefined)
            }}
            onBlur={e => {
              const val = e.target.value
              if (!validateEmail(val)) {
                setEmailError(translates.for('errors.invalid_email') || 'Invalid email')
              } else {
                setEmailError(undefined)
              }
            }}
            placeholder={translates.for('wizards.support_question.email_placeholder')}
          />
          {emailError && <div className='error'>{emailError}</div>}
        </div>

        <div className='form-field'>
          <label>{translates.for('wizards.support_question.phone')}</label>
          <input
            type='text'
            value={wizardData.phoneNumber || ''}
            onChange={(e) => {
              const val = e.target.value
              setWizardData(prev => {
                if (!prev) return prev
                const data = { ...prev, phoneNumber: val }
                SaveWizardData(data)
                return data
              })
              setPhoneError(undefined)
            }}
            onBlur={e => {
              const val = e.target.value
              if (val && !validatePhone(val)) {
                setPhoneError(translates.for('errors.invalid_phone') || 'Phone number must be numeric')
              } else {
                setPhoneError(undefined)
              }
            }}
            placeholder={translates.for('wizards.support_question.phone_placeholder')}
          />
          {phoneError && <div className='error'>{phoneError}</div>}
        </div>
        <div
          className='approve-btn'
          onClick={async () => {
            setError(undefined)
            // Validation!
            if (!validateEmail(wizardData.email)) {
              setEmailError(translates.for('errors.invalid_email') || 'Invalid email')
              return
            }
            if (wizardData.phoneNumber && !validatePhone(wizardData.phoneNumber)) {
              setPhoneError(translates.for('errors.invalid_phone') || 'Phone number must be numeric')
              return
            }
            services.session.fetchWithToken(`/api/public/support/questions/new`, {
              method: 'POST',
              body: JSON.stringify(wizardData),
            }).then(async (r) => {
              if (r.status !== 200) {
                const err = await r.text()
                console.log(err)
                setError(err)
                return
              }
              CleanWizardData()
              setDone(true)
            })
          }}>
          {translates.for(`wizards.support_question.approve`)}
        </div>
      </div> : null}
      {error ? <div className='critic-error-box'>{translates.for('errors.general_error')}</div> : ''}
      {done ? <div className='done-box'>{translates.for('wizards.support_question.done')}</div> : ''}
    </MainPageLayout>
  )
}
